/**
 * Equipment against the zones it sits in. Where zones overlap the more severe classification applies,
 * with the most demanding gas group and temperature class (8.1). The EPL and temperature rules come
 * from IEC 60079-14 / ATEX practice, not from 60079-10-1 (MD-009).
 */
import { aabbOverlap, boxAabb, insideShape, shapeAabb, shapeSdf } from './sdf';
import type { ZoneInstance } from './zones';
import { T_CLASS_MAX_C } from '../engine/substance';
import type { Box, Equipment, GasGroup, Vec3, ZoneNumber } from '../model/types';

export type EquipmentStatus = 'pass' | 'fail' | 'warning' | 'outside';

export interface EquipmentCheck {
  equipmentId: string;
  status: EquipmentStatus;
  /** Zone instances the equipment is inside. */
  inside: string[];
  /** Zone instances within NEAR_M of the equipment but not touching it. */
  near: string[];
  required?: { zone: ZoneNumber; epl: 'Ga' | 'Gb' | 'Gc'; gasGroup?: GasGroup; aitC?: number };
  reasons: string[];
}

export const NEAR_M = 0.1;

/** Indices into boxSamples of corners, edge midpoints, face centres and the centre (grid steps 0, 2, 4). */
const OUTER = [0, 2, 4].flatMap((i) => [0, 2, 4].flatMap((j) => [0, 2, 4].map((k) => i * 25 + j * 5 + k)));

const EPL_RANK = { Ga: 3, Gb: 2, Gc: 1 } as const;
const REQUIRED_EPL = { 0: 'Ga', 1: 'Gb', 2: 'Gc' } as const;
const GROUP_RANK = { IIA: 1, IIB: 2, IIC: 3, II: 3 } as const;

/** Points that cover a box: corners, edge midpoints, face centres and a 5 × 5 × 5 interior grid. */
export function boxSamples(box: Box): Vec3[] {
  const [cx, cy, cz] = box.center;
  const [sx, sy, sz] = box.size;
  const c = Math.cos(box.yawRad);
  const s = Math.sin(box.yawRad);
  const pts: Vec3[] = [];
  const n = 4;
  for (let i = 0; i <= n; i++) {
    for (let j = 0; j <= n; j++) {
      for (let k = 0; k <= n; k++) {
        const lx = (i / n - 0.5) * sx;
        const ly = (j / n - 0.5) * sy;
        const lz = (k / n - 0.5) * sz;
        pts.push([cx + c * lx - s * ly, cy + s * lx + c * ly, cz + lz]);
      }
    }
  }
  return pts;
}

export function checkEquipment(eq: Equipment, zones: ZoneInstance[]): EquipmentCheck {
  const box = boxAabb(eq.box);
  let samples: Vec3[] | undefined;
  const inside: ZoneInstance[] = [];
  const near: ZoneInstance[] = [];
  for (const z of zones) {
    // cheap rejection first: the boxes are further apart than the "near" distance
    if (!aabbOverlap(box, shapeAabb(z.shape), NEAR_M)) continue;
    samples ??= boxSamples(eq.box);
    if (samples.some((p) => insideShape(z.shape, p))) { inside.push(z); continue; }
    // exact distance only for the near-miss warning, on the 27 outer points
    let min = Infinity;
    for (const i of OUTER) min = Math.min(min, shapeSdf(z.shape, samples[i]!));
    if (min <= NEAR_M) near.push(z);
  }

  const base = { equipmentId: eq.id, inside: inside.map((z) => z.id), near: near.map((z) => z.id) };
  if (!inside.length) {
    return near.length
      ? { ...base, status: 'warning', reasons: [`${eq.tag} is within ${NEAR_M * 100} cm of a zone boundary.`] }
      : { ...base, status: 'outside', reasons: [] };
  }

  const zone = Math.min(...inside.map((z) => z.zone)) as ZoneNumber;
  const groups = inside.map((z) => z.gasGroup).filter((g): g is GasGroup => g !== undefined);
  const gasGroup = groups.sort((a, b) => GROUP_RANK[b] - GROUP_RANK[a])[0];
  const aits = inside.map((z) => z.aitC).filter((t): t is number => t !== undefined);
  const aitC = aits.length ? Math.min(...aits) : undefined;
  const required = { zone, epl: REQUIRED_EPL[zone], ...(gasGroup ? { gasGroup } : {}), ...(aitC !== undefined ? { aitC } : {}) };

  const fails: string[] = [];
  const warns: string[] = [];
  if (eq.ex.kind === 'non-ex') {
    fails.push(`${eq.tag} is not Ex-protected but sits inside Zone ${zone}.`);
  } else {
    if (EPL_RANK[eq.ex.epl] < EPL_RANK[required.epl]) {
      fails.push(`Zone ${zone} needs equipment protection level ${required.epl}${zone === 2 ? ' or better' : zone === 1 ? ' or Ga' : ''}; ${eq.tag} is ${eq.ex.epl}.`);
    }
    if (gasGroup) {
      if (GROUP_RANK[eq.ex.gasGroup] < GROUP_RANK[gasGroup]) fails.push(`The zone needs gas group ${gasGroup}; ${eq.tag} is ${eq.ex.gasGroup}.`);
    } else {
      warns.push('The gas group of the substance is not recorded, so the gas group could not be checked.');
    }
    const maxT = eq.ex.maxSurfaceTempC ?? (eq.ex.tClass ? T_CLASS_MAX_C[eq.ex.tClass] : undefined);
    if (maxT === undefined) warns.push(`${eq.tag} has no temperature class or maximum surface temperature recorded.`);
    else if (aitC === undefined) warns.push('The auto-ignition temperature of the substance is not recorded, so the temperature class could not be checked.');
    else if (maxT > aitC) fails.push(`${eq.tag} can reach ${maxT} °C${eq.ex.tClass ? ` (${eq.ex.tClass})` : ''}, above the auto-ignition temperature of ${aitC} °C.`);
  }
  if (fails.length) return { ...base, status: 'fail', required, reasons: [...fails, ...warns] };
  if (warns.length) return { ...base, status: 'warning', required, reasons: warns };
  return { ...base, status: 'pass', required, reasons: [] };
}
